import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowUpRight, Pulse } from "@phosphor-icons/react";

const HERO_IMAGE =
  "https://images.unsplash.com/photo-1517196084897-498e0abd7c2d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzB8MHwxfHNlYXJjaHwxfHxnb2xkJTIwYWJzdHJhY3QlMjBnZW9tZXRyaWMlMjB0ZXh0dXJlfGVufDB8fHx8MTc4MjAxNjgxN3ww&ixlib=rb-4.1.0&q=85";

const lines = [
  { id: "hero-line-1", parts: [{ text: "TI que não para." }] },
  {
    id: "hero-line-2",
    parts: [{ text: "Conexão " }, { text: "estável.", accent: true }],
  },
];

const container = {
  hidden: {},
  show: { transition: { staggerChildren: 0.16, delayChildren: 0.35 } },
};

const lineReveal = {
  hidden: { y: "115%" },
  show: { y: "0%", transition: { duration: 1, ease: [0.16, 1, 0.3, 1] } },
};

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] } },
};

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imageY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const frameY = useTransform(scrollYProgress, [0, 1], ["0%", "10%"]);

  return (
    <section id="top" ref={ref} data-testid="hero-section" className="relative min-h-screen flex items-center pt-[72px] overflow-hidden">
      <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
        <div className="absolute -top-32 -right-32 w-[560px] h-[560px] rounded-full bg-gold/10 blur-3xl" />
        <div className="absolute bottom-0 left-0 w-full h-px bg-black/5" />
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-10 w-full grid lg:grid-cols-12 gap-12 lg:gap-8 items-center py-20 lg:py-0">
        <motion.div variants={container} initial="hidden" animate="show" className="lg:col-span-7 relative z-10">
          <motion.div variants={fadeUp} className="flex items-center gap-3 mb-8">
            <span className="w-2 h-2 rounded-full bg-gold" />
            <span data-testid="hero-eyebrow" className="text-xs font-semibold tracking-[0.3em] uppercase text-gold-dark">
              MSP para pequenas e médias empresas
            </span>
          </motion.div>

          <h1 className="font-display font-black tracking-tighter text-ink text-5xl sm:text-6xl lg:text-[5.5rem] leading-[0.95]">
            {lines.map((line) => (
              <span key={line.id} className="block overflow-hidden pb-1">
                <motion.span variants={lineReveal} className="block" data-testid={line.id}>
                  {line.parts.map((p, i) =>
                    p.accent ? (
                      <span key={i} className="text-gold-dark">{p.text}</span>
                    ) : (
                      <span key={i}>{p.text}</span>
                    )
                  )}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            variants={fadeUp}
            data-testid="hero-subtitle"
            className="mt-8 max-w-xl text-base md:text-lg text-neutral-600 leading-relaxed"
          >
            Gestão de TI com método, organização e monitoramento contínuo — para clínicas,
            restaurantes e escritórios que não podem parar.
          </motion.p>

          <motion.div variants={fadeUp} className="mt-10 flex flex-wrap items-center gap-4">
            <a
              href="#contato"
              data-testid="hero-cta-primary"
              className="group inline-flex items-center gap-2 bg-ink text-[#FAFAFA] font-semibold px-8 py-4 rounded-sm hover:bg-gold hover:text-ink transition-colors duration-300"
            >
              Solicitar orçamento
              <ArrowUpRight size={18} weight="bold" className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
            <a
              href="#servicos"
              data-testid="hero-cta-secondary"
              className="inline-flex items-center gap-2 border border-ink/15 text-ink font-semibold px-8 py-4 rounded-sm hover:border-gold hover:text-gold-dark transition-colors duration-300"
            >
              Ver serviços
              <ArrowDown size={18} weight="bold" />
            </a>
          </motion.div>

          <motion.div variants={fadeUp} className="mt-14 flex items-center gap-10" data-testid="hero-stats">
            {[
              { value: "24/7", label: "Monitoramento" },
              { value: "5+", label: "Funcionários atendidos" },
              { value: "100%", label: "Foco em PMEs" },
            ].map((s) => (
              <div key={s.label}>
                <p className="font-display font-extrabold text-2xl text-ink">{s.value}</p>
                <p className="text-xs text-neutral-500 tracking-wide mt-1">{s.label}</p>
              </div>
            ))}
          </motion.div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.94 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.1, ease: [0.16, 1, 0.3, 1], delay: 0.6 }}
          className="lg:col-span-5 relative hidden lg:block"
        >
          <motion.div style={{ y: frameY }} className="relative">
            <div className="absolute -inset-4 border border-gold/40 rounded-sm" aria-hidden="true" />
            <div className="relative overflow-hidden rounded-sm aspect-[4/5]" data-testid="hero-image-frame">
              <motion.img
                src={HERO_IMAGE}
                alt="Textura geométrica dourada representando precisão e estabilidade"
                data-testid="hero-image"
                style={{ y: imageY, scale: 1.25 }}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-ink/40 via-transparent to-transparent" />
            </div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.3, duration: 0.7 }}
              className="absolute -bottom-6 -left-6 bg-ink text-[#FAFAFA] px-6 py-5 rounded-sm flex items-center gap-4"
              data-testid="hero-uptime-card"
            >
              <Pulse size={28} weight="duotone" className="text-gold" />
              <div>
                <p className="font-display font-extrabold text-xl leading-none">99,9%</p>
                <p className="text-xs text-neutral-400 mt-1 tracking-wide">disponibilidade monitorada</p>
              </div>
            </motion.div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
